/* eslint-disable prefer-promise-reject-errors */
/* eslint-disable class-methods-use-this */
/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-underscore-dangle */

import {
  Component,
  NgZone,
  inject,
  OnDestroy,
  OnInit,
  AfterViewInit,
  Renderer2,
  WritableSignal
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart } from '@angular/router';
import { select } from '@ngrx/store';
import { kebabCase } from 'lodash-es';
import { AppSelector } from './@store/selectors/app.selectors';
import { StsSelectors } from './@store/selectors/sts.selectors';
import { MonitoringService } from './services/apm.service';
import { filter, take, tap, takeUntil, withLatestFrom, distinctUntilChanged } from 'rxjs/operators';
import { combineLatest, Observable, firstValueFrom, Subject } from 'rxjs';
import { ClaimValue } from 'src/app/plasma-ui-common/enums/claim-value.enum';
import { PageTitle } from 'src/app/plasma-ui-common/enums/page-title.enum';
import { UserTypes } from 'src/app/plasma-ui-common/enums/user-type.enum';
import { AuthService } from './services/auth/auth.service';
import { CampaignParamsService } from './services/campaign-params.service';
import { getRedirectUrl, isPrivateRoute, trimUrl } from './utils/common.utils';
import { isCookieBannerHeld, releaseCookieBanner } from './utils/cookie-consent';
import { LoggerService } from './services/logger.service';
import { EStorageKeys } from './models/storage.model';
import { Idle, DEFAULT_INTERRUPTSOURCES, AutoResume } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import OpenReplay from '@openreplay/tracker';
import { AppCommonServices } from './app-common.services';
import { InactivityService } from './services/inactivity.service';
import { NavbarService } from './components/indigo-yellow-ui/services/navbar.service';
import { parseJwt } from './utils/parse-jwt';
import { ThemeNames } from './plasma-ui-common/enums/theme-names.enum';
import { NESTED_TENANT_STYLE_BASE } from './services/config/nested-tenant';
import { UserInfo, UserType } from './plasma-ui-common/services/user-role.service';
import { ClaimTypesEnum } from './plasma-ui-common/enums/claim-types.enum';

/**
 * The live-chat widget, which embed-livechat.js defines only where the tenant has Synchat on.
 * Elsewhere it does not exist, and touching it threw from oidc-client's user-loaded event,
 * failing every sign-in callback - so Columbia never reloaded into its own theme (SA-21292).
 */
const liveChat = (): any => (window as any).SALiveChat;

/** How long the bootstrap loader may cover the page before it is lifted regardless. */
const BOOTSTRAP_LOADER_TIMEOUT_MS = 20000;

/** SA-22130: the CZ tenant's browser-tab title. */
export const CZ_PAGE_TITLE = 'Můj SYNLAB';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [MonitoringService],
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private prevurl: string;
  private readonly store = this.commonService.store;
  private readonly config = this.commonService.config;
  private readonly router = this.commonService.router;
  private readonly transloco = this.commonService.transloco;

  private readonly onDestroy$ = new Subject<void>();

  tracker: OpenReplay;

  isLangLoading$: Observable<boolean>;
  imgs = [];
  forceMFA$ = this.store.pipe(select(StsSelectors.isMFARequired));
  useNewNav$: Observable<boolean> = this.store.pipe(select(StsSelectors.useNewNav));
  isFiTenant$ = this.store.pipe(select(StsSelectors.isFITenant));

  public readonly showMainNavbar: WritableSignal<boolean>;

  public isBluePulseTheme = false;

  private readonly ngZone = inject(NgZone);

  constructor(
    private readonly renderer2: Renderer2,
    private readonly commonService: AppCommonServices,
    private readonly title: Title,
    private readonly auth: AuthService,
    private readonly loggerService: LoggerService,
    private readonly idle: Idle,
    private readonly keepalive: Keepalive,
    private readonly navbarService: NavbarService,
    private readonly inactivityService: InactivityService,
    private readonly campaignParams: CampaignParamsService,
  ) {
    this.showMainNavbar = this.navbarService.showMainNavbar;
    this.setPageClass(this.renderer2);
    this.hideBootstrapLoader(this.renderer2);
    this.releaseHeldCookieBanner();
    this.syncDocumentLanguage(this.renderer2);
    this.setTitle();
    // The UTM parameters of the landing url do not survive the sign-in redirect, so they are
    // kept until /signin-callback puts them back on the route it lands on.
    this.campaignParams.capture();
    this.fetchFreshToken();

    this.setupIdleTimer();
  }

  ngAfterViewInit(): void {
    // schedule change after initial CD to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => this.setSynlabHeaderVisibility(), 0);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  private setSynlabHeaderVisibility() {
    const isBluePulseTheme = this.config._envConfig.ThemeName === ThemeNames.BluePulse;
    this.isBluePulseTheme = isBluePulseTheme;
    if (isBluePulseTheme) {
      this.navbarService.showMainNavbar.set(false);
    }
  }

  private setupIdleTimer() {
    // enable idleTimer in EE and DK
    this.config.envConfig$
      .pipe(
        filter(v => !!v),
        take(1),
        tap(config => {
          if (config.EnableIdleTimeOut.toLowerCase() === ClaimValue.TRUE) {
            const idleTimeTemp = Number(config.idleTimeOutInSeconds) || 900;
            const idleTime = Number(idleTimeTemp);

            this.loggerService.log({ idleTime });
            this.enableIdleTimer(idleTime);
          }

          this.setCountries();
        }),
      )
      .subscribe();
  }

  /**
   * Keeps <html lang> in step with the active language.
   *
   * index.html ships `lang="en"` and nothing ever updated it, so a screen reader announced
   * every tenant's pages with English pronunciation rules (WCAG 3.1.1 / 3.1.2). Transloco
   * emits the current language on subscribe, so this covers the initial load too.
   */
  private syncDocumentLanguage(r: Renderer2) {
    this.transloco.langChanges$
      .pipe(
        takeUntil(this.onDestroy$),
        tap((language) => {
          if (language) r.setAttribute(document.documentElement, 'lang', language);
        }),
      )
      .subscribe();
  }

  private setCountries() {
    const changes$ = this.transloco.langChanges$;
    // eslint-disable-next-line no-underscore-dangle
    const authUrl: string = this.config._envConfig?.BaseAuthUrl;
    // if language changes reload country dropdowns with correct active-language http header
    changes$
      .pipe(
        tap(async () => {
          await firstValueFrom(this.config.getCountries(authUrl));
        }),
      )
      .subscribe();
  }

  signout() {
    this.auth.startSignoutMainWindow();
  }

  private preloadImages() {
    this.pload(
      'assets/images/spinner.gif',
      'assets/images/activation-link-used.png',
      'assets/images/success-icon.png',
    );
  }

  // enable OpenReplay
  private enableOpenReplay() {
    this.store
      .pipe(select(StsSelectors.getUserUserId))
      .pipe(
        withLatestFrom(
          this.config.envConfig$,
          this.store.pipe(select(StsSelectors.getEnableOpenReplay)),
          this.store.pipe(select(StsSelectors.getAppEnvironment)),
        ),
        tap(([userId, config, openReplayEnabled, env]) => {
          this.loggerService.log({ config, openReplayEnabled, userId, env });
          if (userId) {
            const { OpenReplayProjectKey, OpenReplayIngestPoint } = config;
            this.loggerService.log({ OpenReplayProjectKey, OpenReplayIngestPoint });
            if (openReplayEnabled) {
              this.tracker = new OpenReplay({
                projectKey: OpenReplayProjectKey,
                ingestPoint: OpenReplayIngestPoint,
              });
              this.tracker.start({ userID: userId, metadata: { environment: env } });
            } else if (this.tracker?.isActive) {
              this.tracker.stop();
            }
          }
        }),
      )
      .subscribe();
  }

  private subscribeToUserClaims() {
    this.store.pipe(select(StsSelectors.getClaims)).subscribe(claims => {
      if (claims) {
        const userTypes = claims.filter(claim => claim.claimType === 'user_type').map(claim => claim.claimValue);

        if (userTypes.includes(UserType.ResultsViewer) && (!this.router.url.startsWith('/setup-mfa') && !this.router.url.startsWith('/register'))) {
          this.router.navigateByUrl('/unauthorized');
        }
      }
    });
  }

  ngOnInit() {
    this.subscribeToUserClaims();
    this.preloadImages();

    this.enableOpenReplay();

    setTimeout(() => {
      this.isLangLoading$ = this.store.pipe(select(AppSelector.isLanguageLoading));
    }, 0);

    this.isFiTenant$.pipe(take(1)).subscribe(isFi => {
      if (isFi) {
        this.inactivityService.setupActivityListeners();
      }
    });
    this.setupTwoWayMessaging();
  }

  private setupTwoWayMessaging() {
    this.auth._manager.events.addUserLoaded(user => {
      liveChat()?.updateToken(user.access_token);
    });

    this.auth.userInfo$
      .pipe(distinctUntilChanged((prev, curr) => !!prev === !!curr))
      .subscribe(userInfo => {
        const enableSynchat =
          this.config._envConfig.EnableSynchat.toLowerCase() === ClaimValue.TRUE;
        if (!enableSynchat) return;

        this.initLiveChat(userInfo);
      });
  }

  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;

      script.onload = () => resolve();
      script.onerror = () => reject(`Failed to load script: ${src}`);

      document.body.appendChild(script);
    });
  }

  private async initLiveChat(userInfo: UserInfo) {
    if (
      userInfo &&
      (userInfo.user_type === UserTypes.SYNLAB_ACCESS_USER ||
        userInfo.user_type.includes(UserTypes.SYNLAB_ACCESS_USER))
    ) {
      const oidcKey = Object.keys(sessionStorage).find(key => key.includes('oidc.user:'));
      const token = JSON.parse(sessionStorage.getItem(oidcKey));
      const { family_name: familyName, given_name: givenName } = userInfo;
      const { synlab_id: synlabId } = parseJwt(token.access_token);
      const { BaseSynchatUrl } = this.config._envConfig;

      // Inject script here
      await this.loadScript(`${BaseSynchatUrl}/embed-livechat.js`);

      liveChat()?.initialize({
        serverUrl: BaseSynchatUrl,
        token: token.access_token,
        userName: `${givenName} ${familyName}`,
        synlabId,
      });
    } else {
      liveChat()?.remove();
    }
  }

  pload(...args: string[]): void {
    for (let i = 0; i < args.length; i += 1) {
      this.imgs[i] = new Image();
      this.imgs[i].src = args[i];
    }
  }

  setTitle() {
    combineLatest([
      this.store.pipe(select(StsSelectors.isEstoniaInstance)),
      this.store.pipe(select(StsSelectors.isCzechRepublic)),
      this.store.pipe(select(StsSelectors.isAppWhiteLabeled)),
    ])
      .pipe(
        take(1),
        tap(([isEETenant, isCZTenant, isWhiteLabeled]) => {
          let title: string = PageTitle.DEFAULT;
          if (isWhiteLabeled && isEETenant) {
            title = PageTitle.ESTONIA;
          }
          // SA-22130: the CZ tenant goes by its local brand name.
          if (isCZTenant) {
            title = CZ_PAGE_TITLE;
          }

          this.title.setTitle(title);
        }),
      )
      .subscribe();
  }

  /**
   * Takes down the index.html loader once the router has settled, so the blank window between
   * bootstrap and the first rendered route is covered.
   *
   * Cancel and error count as settled too: a guard that redirects away from the requested route
   * never emits NavigationEnd, and leaving the loader up would strand the user behind it.
   */
  private hideBootstrapLoader(r: Renderer2) {
    this.router.events
      .pipe(
        filter(
          event =>
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError,
        ),
        take(1),
        tap(() => r.addClass(document.body, 'app-ready')),
        takeUntil(this.onDestroy$),
      )
      .subscribe();

    // A first navigation that never settles (a resolver waiting on an API that does not
    // answer) must not keep the page covered for good either (PR #2538). Outside the zone, so
    // the pending timer does not hold up the app's stability.
    this.ngZone.runOutsideAngular(() =>
      setTimeout(() => document.body.classList.add('app-ready'), BOOTSTRAP_LOADER_TIMEOUT_MS),
    );
  }

  /**
   * Lets the consent banner show once a route other than /signin-callback has settled.
   *
   * index.html holds it back on routes behind sign-in. Those only reach NavigationEnd once
   * AuthGuard has let the user through - a signed-out visit is cancelled and sent to the STS -
   * so the first settled route means the user is signed in, or is on a public route.
   * /signin-callback is skipped because it is still mid sign-in and moves on by itself.
   */
  private releaseHeldCookieBanner() {
    if (!isCookieBannerHeld()) return;

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd =>
            event instanceof NavigationEnd &&
            !event.urlAfterRedirects.toLowerCase().startsWith('/signin-callback'),
        ),
        take(1),
        tap(() => releaseCookieBanner()),
        takeUntil(this.onDestroy$),
      )
      .subscribe();
  }

  setPageClass(r: Renderer2) {
    this.router.events
      .pipe(
        withLatestFrom(this.store.select(StsSelectors.getWhiteLabelName)),
        tap(([event, whiteLabelName]) => {
          // set class based on tenant name
          if (whiteLabelName) {
            // A nested tenant carries the class of the tenant it borrows its stylesheets from
            // first, so its own class below can override it.
            const styleBase = NESTED_TENANT_STYLE_BASE[whiteLabelName];
            if (styleBase) {
              r.addClass(document.body, `tenant-${kebabCase(styleBase)}`);
            }

            r.addClass(document.body, `tenant-${kebabCase(whiteLabelName)}`);
          }

          // set body class based on route path
          if (event instanceof NavigationStart) {
            if (this.prevurl) {
              r.removeClass(document.body, this.prevurl);
            }
            const currentUrlSlug = kebabCase(event.url.slice(1) || '');
            if (currentUrlSlug) {
              r.addClass(document.body, currentUrlSlug);
            }
            this.prevurl = currentUrlSlug;
          }
        }),
      )
      .subscribe();
  }

  // eslint-disable-next-line max-lines-per-function
  private fetchFreshToken() {
    const referrer = trimUrl(window.document?.referrer);
    const origin = window.location?.host;

    this.loggerService.log({ from: referrer, to: origin });
    const redirect = getRedirectUrl();
    if (referrer !== origin) {
      const routes = this.router.config;
      const privateRoute = isPrivateRoute(redirect, routes);
      this.loggerService.log(
        'Check if redirectURL is private route before calling startSigninMainWindow():',
        {
          privateRoute,
          redirect,
        },
      );

      if (privateRoute) {
        document.querySelector('body').classList.add('signin-redirecting');
        this.auth.signinRedirectTrigger$.next(true);
        if (redirect) {
          localStorage.setItem(EStorageKeys.REDIRECT_URL, redirect);
        }
      }
    } else if (
      redirect.startsWith('/account/notification-settings') ||
      redirect === '/setup-mfa' ||
      redirect === '/register/private-complete-details'
    ) {
      localStorage.setItem(EStorageKeys.REDIRECT_URL, redirect);
    }
  }

  private enableIdleTimer(idleTimerInSeconds: number) {
    this.idle.setIdle(30); // how long can they be inactive before considered idle, in seconds
    this.idle.setTimeout(idleTimerInSeconds); // how long can they be idle before considered timed out, in seconds
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.setAutoResume(AutoResume.idle);
    this.idle.onTimeout.subscribe(() => {
      this.loggerService.log({ onTimeout: true });
      this.auth
        .isLoggedInObs()
        .pipe(
          take(1),
          tap(isLoggedIn => {
            this.loggerService.log({ isLoggedIn });
            if (isLoggedIn) {
              this.auth.startSignoutMainWindow();
            }
          }),
        )
        .subscribe();
    });
    this.idle.onIdleStart.subscribe(() => this.loggerService.log("You've gone idle!"));
    this.idle.onTimeoutWarning.subscribe(countdown =>
      this.loggerService.log(`You will time out in ${countdown} seconds!`),
    );

    this.keepalive.interval(15);
    this.keepalive.onPing.subscribe(() => this.loggerService.log('User is idle!'));

    // right when the component initializes, start reset state and start watching
    this.reset();
  }

  reset() {
    // we'll call this method when we want to start/reset the idle process
    // reset any component state and be sure to call idle.watch()
    this.idle.watch();
  }

}
